import {
  answerFromStoredMessage,
  newProposal,
  TeamAssistV2ProposalDecision,
} from 'actions/team_assist_v2/lib/team_assist_v2_answer';
import TeamAssistRating from 'models/team_assist_rating';

/**
 * The turns of a stored session, from the load route's `messages` and
 * `ratings`: each agent question with its answer, the answers that followed
 * an action the agent ran, any surfaced tool results and proposals, and the
 * agent's rating on each answer. glados returns only answered questions, so a
 * question without an answer is left out.
 */
export default function mapTeamAssistV2Session(messages = [], ratings = []) {
  const ratingsByMessageId = {};
  ratings.forEach(rating => {
    const value = RATING_VALUES_BY_THUMBS[rating.thumbs];
    if (rating.messageId && value) {
      ratingsByMessageId[rating.messageId] = {
        value,
        ...(rating.reason ? { reason: rating.reason } : {}),
        ...(rating.comment ? { comment: rating.comment } : {}),
      };
    }
  });

  const answersByQuestionId = {};
  const executionsByToolUseId = {};
  const proposalsByToolUseId = {};
  messages.forEach(message => {
    if (message.role === 'assistant' && message.questionId) {
      answersByQuestionId[message.questionId] = [...(answersByQuestionId[message.questionId] || []), message];
    } else if (message.role === 'tool' && message.kind === 'tool_executed') {
      executionsByToolUseId[message.toolUseId] = {
        result: message.result,
        resultType: message.resultType || null,
        isError: message.isError === true,
      };
    } else if (message.role === 'tool' && message.kind === 'tool_proposed') {
      proposalsByToolUseId[message.toolUseId] = newProposal(message);
    }
  });
  // A decision only means something for a proposal the session holds.
  messages.forEach(message => {
    const proposal = message.role === 'tool' && proposalsByToolUseId[message.toolUseId];
    if (proposal) {
      proposalsByToolUseId[message.toolUseId] = withDecision(proposal, message);
    }
  });

  const storedAnswer = message =>
    answerFromStoredMessage(message, {
      executionsByToolUseId,
      proposalsByToolUseId,
      rating: ratingsByMessageId[message.id] || null,
    });

  return messages
    .filter(message => message.role === 'agent' && answersByQuestionId[message.id])
    .map(question => {
      const [answer, ...followUps] = answersByQuestionId[question.id];
      return {
        id: question.id,
        question: (question.question && question.question.text) || '',
        displayedMessage: question.displayedMessage,
        conversationId: question.conversationId,
        answer: storedAnswer(answer),
        followUps: followUps.map(storedAnswer),
        live: false,
      };
    });
}

// The agent's decision from a `tool_approved`, `tool_rejected`, or approved
// `tool_executed` message; `tool_executed` carries `decidedBy` only for a run
// the agent approved.
function withDecision(proposal, message) {
  if (message.kind === 'tool_rejected') {
    return { ...proposal, decision: TeamAssistV2ProposalDecision.rejected };
  }
  if (message.kind === 'tool_approved') {
    return { ...proposal, decision: TeamAssistV2ProposalDecision.approved, approvedInput: message.approvedInput };
  }
  if (message.kind === 'tool_executed' && message.decidedBy) {
    return { ...proposal, decision: TeamAssistV2ProposalDecision.approved, approvedInput: message.executedInput };
  }
  return proposal;
}

const RATING_VALUES_BY_THUMBS = {
  up: TeamAssistRating.Value.thumbs_up,
  down: TeamAssistRating.Value.thumbs_down,
};
